/**
 * Client adapter for the aira-rag-backend, mirroring the Gmail BFF's
 * apps/gmail-addon/src/backend/backend.ts. Maps the async run result back into
 * the legacy `V1DraftResponse` shape so the existing task-pane components and
 * the useDraftRequest state machine keep working unchanged.
 *
 * AUTH: the backend verifies an Identity Platform (Firebase Auth) ID token. The
 * task pane holds a Firebase session (see src/lib/auth.tsx) and getAccessToken()
 * returns a fresh ID token from it. The Office Dialog window has no session, so
 * the parent injects a short-lived token via setInjectedToken() (handshake in
 * dialog.tsx / App.tsx). See docs/authentication.md and docs/outlook-entra-setup.md.
 */
import {
  confidenceToBadge,
  type IntentType,
  isProgressGroupStatus,
  mapOrchestratorSlugToV1Intent,
  type ProgressGroupStatus,
  type ProgressStep,
  planSteps,
  type RunEvent,
  resolveMissingEntitiesFromBranches,
  STATUS,
  toDraftCitations,
  type V1DraftResponse,
} from '@aira/shared';
import { getFirebaseAuth } from '../lib/firebase';
import { waitForFirebaseUser } from '../lib/firebaseAuthState';
import { getMailboxEmail } from '../lib/microsoftAuth';

// Re-export so existing imports (useDraftRequest, App.tsx) keep working.
export type { ProgressStep } from '@aira/shared';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? '';
function outlookHeaders(): Record<string, string> {
  const mailboxEmail = getMailboxEmail() ?? injectedMailboxEmail;
  return {
    'x-channel': 'outlook',
    ...(mailboxEmail ? { 'x-outlook-mailbox-email': mailboxEmail } : {}),
  };
}
const DRAFT_RUN_TIMEOUT_MS = 300_000;
const RATE_LIMIT_FALLBACK_MESSAGE =
  'Too many draft requests right now. Please wait a moment and try again.';

async function draftRequestErrorFromResponse(res: Response, endpoint: string): Promise<Error> {
  const raw = await res.text().catch(() => '');
  let code: string | null = null;
  let backendMessage: string | null = null;
  try {
    const parsed = JSON.parse(raw) as { error?: unknown; message?: unknown };
    if (typeof parsed.error === 'string') code = parsed.error;
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      backendMessage = parsed.message.trim();
    }
  } catch {
    // Non-JSON proxy errors retain the raw HTTP diagnostic below.
  }

  if (code === 'activation_required') return new Error(STATUS.activationRequired);
  if (code === 'rate_limit_exceeded') {
    return new Error(backendMessage ?? RATE_LIMIT_FALLBACK_MESSAGE);
  }
  return new Error(`${endpoint} failed: HTTP ${res.status} ${raw}`);
}

// The Office Dialog window (dialog.tsx) runs in a separate browser context that
// does NOT share the task pane's Firebase session, so it cannot mint its own ID
// token. The parent hands it a short-lived token over the Dialog messaging
// channel; the dialog stashes it here so the shared start/poll helpers below
// authenticate without a Firebase session.
let injectedToken: string | null = null;
let injectedMailboxEmail: string | null = null;
export function setInjectedToken(token: string | null): void {
  injectedToken = token;
}

export function setInjectedMailboxEmail(email: string | null): void {
  injectedMailboxEmail = email?.trim().toLowerCase() || null;
}

export class NotAuthenticatedError extends Error {
  constructor() {
    super('Not signed in — cannot call the AIRA backend');
    this.name = 'NotAuthenticatedError';
  }
}

export interface DraftReadinessProbe {
  draftReady: boolean;
  reasonCodes: string[];
  message?: string;
}

export interface ProgressGroup {
  key: string;
  label: string;
  status: ProgressGroupStatus;
  phases: string[];
  durationMs: number | null;
}

export interface DraftHistoryItem {
  runId: string;
  status: string;
  draftSubject: string | null;
  draftPreview: string;
  citationCount: number;
  createdAt: string;
  finishedAt: string | null;
}

export async function fetchDraftReadiness(
  timeoutMs = 10_000,
  forceRefreshToken = false,
): Promise<DraftReadinessProbe> {
  const token = await getAccessToken(forceRefreshToken);
  const r = await fetch(`${BACKEND_URL}/v1/drafts/readiness`, {
    headers: { ...outlookHeaders(), authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(timeoutMs),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: unknown; code?: unknown };
    const message = typeof body.error === 'string' ? body.error : `HTTP ${r.status}`;
    const code =
      typeof body.code === 'string'
        ? body.code
        : message.toLowerCase().includes('microsoft tenant')
          ? 'microsoft_tenant_not_allowed'
          : message.toLowerCase().includes('microsoft email domain')
            ? 'microsoft_domain_not_allowed'
            : r.status === 403
              ? 'activation_required'
              : 'readiness_unavailable';
    if (r.status === 403) {
      return { draftReady: false, reasonCodes: [code], message };
    }
    throw new Error(`GET /v1/drafts/readiness failed: ${message}`);
  }
  const data = (await r.json()) as { draft_ready?: boolean; reason_codes?: unknown };
  return {
    draftReady: data.draft_ready !== false,
    reasonCodes: Array.isArray(data.reason_codes)
      ? data.reason_codes.filter((c): c is string => typeof c === 'string')
      : [],
  };
}

export async function fetchDraftHistoryByKey(
  key: string,
  timeoutMs = 6000,
): Promise<DraftHistoryItem[]> {
  const token = await getAccessToken();
  const params = new URLSearchParams({ key, limit: '50' });
  const r = await fetch(`${BACKEND_URL}/v1/drafts/history?${params.toString()}`, {
    headers: { ...outlookHeaders(), authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(timeoutMs),
  });
  if (!r.ok) throw new Error(`GET /v1/drafts/history failed: HTTP ${r.status}`);
  const body = (await r.json()) as {
    items?: Array<{
      run_id: string;
      status: string;
      draft_subject: string | null;
      draft_preview: string;
      citation_count: number;
      created_at: string;
      finished_at: string | null;
    }>;
  };
  return (body.items ?? []).map((item) => ({
    runId: item.run_id,
    status: item.status,
    draftSubject: item.draft_subject,
    draftPreview: item.draft_preview,
    citationCount: item.citation_count,
    createdAt: item.created_at,
    finishedAt: item.finished_at,
  }));
}

export async function fetchDraftRunById(
  runId: string,
  token?: string,
  timeoutMs = 12000,
): Promise<V1DraftResponse> {
  const bearer = token ?? (await getAccessToken());
  const r = await fetch(`${BACKEND_URL}/v1/drafts/${runId}`, {
    headers: { ...outlookHeaders(), authorization: `Bearer ${bearer}` },
    signal: AbortSignal.timeout(timeoutMs),
  });
  if (!r.ok) throw new Error(`GET /v1/drafts/:id failed: HTTP ${r.status}`);
  return toV1DraftResponse((await r.json()) as DraftRunResult);
}

export interface DraftRunSnapshot {
  response: V1DraftResponse;
  progress: ProgressStep[];
  groupedProgress: ProgressGroup[];
}

export function createDraftRefinementOperationId(): string {
  return crypto.randomUUID();
}

async function fetchProgressSteps(runId: string, token: string): Promise<ProgressStep[]> {
  try {
    const result = await fetch(`${BACKEND_URL}/v1/drafts/${runId}/events`, {
      headers: { ...outlookHeaders(), authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(6000),
    });
    if (!result.ok) return [];
    const body = (await result.json()) as EventsResponse;
    return planSteps(body.events ?? []);
  } catch {
    return [];
  }
}

/** Restore a completed run together with the persisted progress shown during generation. */
export async function fetchDraftRunSnapshot(runId: string): Promise<DraftRunSnapshot> {
  const token = await getAccessToken();
  const [response, progress, groupedProgress] = await Promise.all([
    fetchDraftRunById(runId, token),
    fetchProgressSteps(runId, token),
    fetchGroupedProgress(runId, token).catch(() => []),
  ]);
  return { response, progress, groupedProgress };
}

/**
 * Refine the current run in place, preserving the original customer email,
 * classification, citations, and history identity. `draftBody` is the text
 * currently visible in the editor, including any manual changes.
 */
export async function refineDraftRun(
  runId: string,
  instruction: string,
  draftBody: string,
  operationId = createDraftRefinementOperationId(),
): Promise<V1DraftResponse> {
  const token = await getAccessToken();
  let r: Response;
  try {
    r = await fetch(`${BACKEND_URL}/v1/drafts/${runId}/refine`, {
      method: 'POST',
      headers: {
        ...outlookHeaders(),
        authorization: `Bearer ${token}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({ instruction, draft_body: draftBody, operation_id: operationId }),
      signal: AbortSignal.timeout(30_000),
    });
  } catch (err) {
    const name = err instanceof Error ? err.name : '';
    if (name !== 'AbortError' && name !== 'TimeoutError') throw err;
    // The server does not cancel work when the browser's request deadline
    // expires. Rejoin this specific refinement so a restored prior draft is
    // never mistaken for a successful result from the timed-out operation.
    return pollDraftRefinement(runId, token, operationId);
  }
  if (!r.ok) {
    throw await draftRequestErrorFromResponse(r, 'POST /v1/drafts/:id/refine');
  }
  return fetchDraftRunById(runId, token);
}

export async function fetchGroupedProgress(
  runId: string,
  token?: string,
  timeoutMs = 6000,
): Promise<ProgressGroup[]> {
  const bearer = token ?? (await getAccessToken());
  const r = await fetch(`${BACKEND_URL}/v1/drafts/${runId}/progress/grouped`, {
    headers: { ...outlookHeaders(), authorization: `Bearer ${bearer}` },
    signal: AbortSignal.timeout(timeoutMs),
  });
  if (!r.ok) return [];
  const body = (await r.json()) as {
    groups?: Array<{
      key: string;
      label: string;
      status: string;
      phases?: string[];
      duration_ms: number | null;
    }>;
  };
  return (body.groups ?? []).map((g) => ({
    key: g.key,
    label: g.label,
    status: isProgressGroupStatus(g.status) ? g.status : 'pending',
    phases: Array.isArray(g.phases) ? g.phases : [],
    durationMs: g.duration_ms ?? null,
  }));
}

/**
 * Bearer token for the RAG backend. Prefers a fresh Identity Platform ID token
 * from the task pane's Firebase session; falls back to a parent-injected token
 * when running inside the Office Dialog window (which has no session).
 */
export async function getAccessToken(forceRefresh = false): Promise<string> {
  const auth = getFirebaseAuth();
  if (auth.currentUser) return auth.currentUser.getIdToken(forceRefresh);
  // The dialog receives a short-lived token from its parent and has no local
  // Firebase session to restore. Do not make it wait for an impossible user.
  if (injectedToken) return injectedToken;
  const user = await waitForFirebaseUser(auth);
  if (user) return user.getIdToken(forceRefresh);
  throw new NotAuthenticatedError();
}

/**
 * Persist Like/Dislike on the draft's history row — parity with the Gmail
 * add-on's markDraftHistoryFeedback. `runId` = V1DraftResponse.trace_id.
 * Optional `note` is saved to staff_training_notes so the AIRA Product
 * department can review it in All Conversations.
 * 404 = no history row yet → ignore (the local UI acknowledgement still stands).
 */
export async function markDraftFeedback(
  runId: string,
  feedback: 'like' | 'dislike',
  options?: { note?: string },
): Promise<void> {
  const token = await getAccessToken();
  const trimmedNote = options?.note?.trim();
  const body: {
    run_id: string;
    feedback: 'like' | 'dislike';
    note?: string;
  } = { run_id: runId, feedback };
  if (trimmedNote) {
    body.note = trimmedNote;
  }
  const r = await fetch(`${BACKEND_URL}/v1/draft-history/mark-feedback`, {
    method: 'POST',
    headers: {
      ...outlookHeaders(),
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(8000),
  });
  if (r.status === 404) return;
  if (!r.ok) throw new Error(`POST /v1/draft-history/mark-feedback failed: HTTP ${r.status}`);
}

/** Record that the merchant inserted this run's draft into the Outlook reply. */
export async function markDraftInserted(runId: string): Promise<void> {
  const token = await getAccessToken();
  const r = await fetch(`${BACKEND_URL}/v1/draft-history/mark-inserted`, {
    method: 'POST',
    headers: {
      ...outlookHeaders(),
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify({ run_id: runId }),
    signal: AbortSignal.timeout(8000),
  });
  if (r.status === 404) return;
  if (!r.ok) throw new Error(`POST /v1/draft-history/mark-inserted failed: HTTP ${r.status}`);
}

interface DraftRunResult {
  run_id: string;
  status: string;
  draft: { subject?: string; body?: string; language?: string } | null;
  guardrail?: { ok?: boolean; issues?: unknown[] } | null;
  citations?: unknown[];
  knowledge_base_url?: string | null;
  intent_branches?: Array<{
    intent_slug?: string;
    confidence?: number;
    is_out_of_scope?: boolean;
    status?: 'pending' | 'running' | 'completed' | 'failed' | 'out_of_scope' | 'missing_entity';
    missing_entities?: unknown;
    extracted_entities?: Record<string, string>;
  }>;
  error?: { code?: string; message?: string } | null;
  draft_feedback?: 'like' | 'dislike' | null;
}

export interface RunDraftInput {
  emailBody: string;
  emailSubject?: string;
  senderEmail?: string;
  /** Display name from the inbound From header (used for greeting). */
  senderName?: string;
  emailMessageId?: string;
  emailReceivedAt?: string;
  forceNew?: boolean;
  extraInstructions?: string;
}

// ProgressStep + the phase→label map + the reducer live in @aira/shared so
// Gmail and Outlook can't drift. (ProgressStep re-exported above.)
interface DraftRunEvent extends RunEvent {
  message?: string | null;
  payload?: Record<string, unknown> | null;
}

interface EventsResponse {
  events: DraftRunEvent[];
  status: string;
  current_phase?: string;
}

export async function pollDraftRefinement(
  runId: string,
  token: string,
  operationId: string,
  timeoutMs = DRAFT_RUN_TIMEOUT_MS,
): Promise<V1DraftResponse> {
  const auth = { ...outlookHeaders(), authorization: `Bearer ${token}` };
  const deadline = Date.now() + timeoutMs;
  let cursor = 0;

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const response = await fetch(`${BACKEND_URL}/v1/drafts/${runId}/events?after_id=${cursor}`, {
      headers: auth,
      signal: AbortSignal.timeout(12_000),
    });
    if (!response.ok) continue;

    const data = (await response.json()) as EventsResponse;
    for (const event of data.events ?? []) {
      cursor = Math.max(cursor, event.id);
      if (
        event.phase !== 'refineDraft' ||
        event.payload?.operation_id !== operationId ||
        event.status === 'started'
      ) {
        continue;
      }
      if (event.status === 'failed') {
        throw new Error(event.message || 'Draft refinement failed.');
      }
      return fetchDraftRunById(runId, token);
    }
  }

  throw new Error('Draft refinement produced no result before timeout');
}

/**
 * Start a draft run on the backend. Returns the run_id + the bearer token
 * used so callers can immediately hand the runId off to another surface
 * (e.g. the Office Dialog popup) without re-authenticating.
 *
 * Split out of `runDraft` so the dialog can attach to a run that the taskpane
 * already started, instead of POSTing a second, duplicate run.
 */
export async function startDraftRun(
  input: RunDraftInput,
): Promise<{ runId: string; token: string }> {
  const token = await getAccessToken();
  const body = input.extraInstructions
    ? `${input.emailBody}\n\n[Agent instruction: ${input.extraInstructions}]`
    : input.emailBody;

  const startRes = await fetch(`${BACKEND_URL}/v1/drafts`, {
    method: 'POST',
    headers: {
      ...outlookHeaders(),
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      email_subject: input.emailSubject ?? '',
      email_body: body,
      email_message_id: input.emailMessageId || undefined,
      email_received_at: input.emailReceivedAt || undefined,
      force_new: input.forceNew || undefined,
      email_from: input.senderEmail?.includes('@')
        ? {
            email: input.senderEmail,
            ...(input.senderName?.trim() ? { name: input.senderName.trim() } : {}),
          }
        : undefined,
      customer_first_name: input.senderName?.trim().split(/\s+/)[0] || undefined,
      channel: 'email',
      source: 'outlook',
    }),
    signal: AbortSignal.timeout(12000),
  });
  if (!startRes.ok) {
    throw await draftRequestErrorFromResponse(startRes, 'POST /v1/drafts');
  }
  const { run_id } = (await startRes.json()) as { run_id: string };
  return { runId: run_id, token };
}

/**
 * Poll an existing draft run to a terminal state. 1s cadence, accumulates
 * events via the shared planSteps reducer, then fetches the final V1
 * snapshot. No hardcoded steps — every progress entry is a real graph phase.
 */
export async function pollDraftRun(
  runId: string,
  token: string,
  onProgress?: (steps: ProgressStep[]) => void,
  onGroupedProgress?: (groups: ProgressGroup[]) => void,
): Promise<V1DraftResponse> {
  const auth = { ...outlookHeaders(), authorization: `Bearer ${token}` };
  const allEvents: RunEvent[] = [];
  let cursor = 0;
  const deadline = Date.now() + DRAFT_RUN_TIMEOUT_MS;
  let terminal = false;
  while (Date.now() < deadline && !terminal) {
    await new Promise((r) => setTimeout(r, 1000));
    const r = await fetch(`${BACKEND_URL}/v1/drafts/${runId}/events?after_id=${cursor}`, {
      headers: auth,
      signal: AbortSignal.timeout(12000),
    });
    if (!r.ok) continue;
    const data = (await r.json()) as EventsResponse;
    for (const ev of data.events ?? []) {
      cursor = Math.max(cursor, ev.id);
      allEvents.push(ev);
    }
    if (data.events?.length && onProgress) onProgress(planSteps(allEvents));
    if (data.events?.length && onGroupedProgress) {
      void fetchGroupedProgress(runId, token)
        .then(onGroupedProgress)
        .catch(() => {});
    }
    if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
      terminal = true;
    }
  }

  const res = await fetch(`${BACKEND_URL}/v1/drafts/${runId}`, {
    headers: auth,
    signal: AbortSignal.timeout(12000),
  });
  if (!res.ok) throw new Error(`GET /v1/drafts/:id failed: HTTP ${res.status}`);
  const result = (await res.json()) as DraftRunResult;
  if (onGroupedProgress) {
    void fetchGroupedProgress(runId, token)
      .then(onGroupedProgress)
      .catch(() => {});
  }
  if (!terminal && result.status !== 'completed' && result.status !== 'failed') {
    throw new Error('Draft run produced no result before timeout');
  }
  return toV1DraftResponse(result);
}

/**
 * Stream a run's progress over SSE (GET /v1/drafts/:id/progress). Unlike Gmail
 * (Card Service can't hold a connection), the Outlook task pane is a real
 * browser, so it consumes the live event stream with fetch + ReadableStream —
 * EventSource can't send the Bearer header, fetch can. Real graph events feed
 * the shared planSteps reducer AS THEY ARRIVE (no 1s poll gap); on the terminal
 * `done` frame we read the V1 snapshot. Throws on any transport error so the
 * caller can fall back to polling.
 */
export async function streamDraftProgress(
  runId: string,
  token: string,
  onProgress?: (steps: ProgressStep[]) => void,
  onGroupedProgress?: (groups: ProgressGroup[]) => void,
): Promise<V1DraftResponse> {
  const res = await fetch(`${BACKEND_URL}/v1/drafts/${runId}/progress`, {
    headers: {
      ...outlookHeaders(),
      authorization: `Bearer ${token}`,
      accept: 'text/event-stream',
    },
    signal: AbortSignal.timeout(180_000),
  });
  if (!res.ok || !res.body) {
    throw new Error(`GET /v1/drafts/:id/progress failed: HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const events: RunEvent[] = [];
  let buffer = '';
  let terminal = false;

  const handleFrame = (frame: string) => {
    let eventType = 'message';
    let data = '';
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) eventType = line.slice(6).trim();
      else if (line.startsWith('data:')) data += line.slice(5).trim();
    }
    if (!data) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return;
    }
    if (eventType === 'event') {
      events.push(parsed as RunEvent);
      onProgress?.(planSteps(events));
      if (onGroupedProgress) {
        void fetchGroupedProgress(runId, token)
          .then(onGroupedProgress)
          .catch(() => {});
      }
    } else if (eventType === 'done') {
      terminal = true;
    }
  };

  try {
    while (!terminal) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line (\n\n).
      let sep = buffer.indexOf('\n\n');
      while (sep !== -1) {
        handleFrame(buffer.slice(0, sep));
        buffer = buffer.slice(sep + 2);
        sep = buffer.indexOf('\n\n');
      }
    }
  } finally {
    try {
      await reader.cancel();
    } catch {
      /* stream already closed */
    }
  }

  const r = await fetch(`${BACKEND_URL}/v1/drafts/${runId}`, {
    headers: { ...outlookHeaders(), authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(12000),
  });
  if (!r.ok) throw new Error(`GET /v1/drafts/:id failed: HTTP ${r.status}`);
  if (onGroupedProgress) {
    void fetchGroupedProgress(runId, token)
      .then(onGroupedProgress)
      .catch(() => {});
  }
  return toV1DraftResponse((await r.json()) as DraftRunResult);
}

/**
 * Convenience: start + poll, in one call. Preserved for callers that want
 * fire-and-forget semantics (the dialog handoff uses startDraftRun + a
 * stand-alone pollDraftRun in the popup window so the two surfaces share a
 * single backend run).
 */
export async function runDraft(
  input: RunDraftInput,
  onProgress?: (steps: ProgressStep[]) => void,
): Promise<V1DraftResponse> {
  const { runId, token } = await startDraftRun(input);
  return pollDraftRun(runId, token, onProgress);
}

export function toV1DraftResponse(run: DraftRunResult): V1DraftResponse {
  const primary = run.intent_branches?.[0];
  const slug = primary?.intent_slug ?? 'unknown';
  const intent: IntentType = mapOrchestratorSlugToV1Intent(slug);
  // Pass backend confidence through unchanged. If the backend did not emit one
  // we keep it as 0 (the schema requires a number) and surface that via the
  // 'unknown' badge below — never invent a default that misleads the user.
  const rawConfidence = typeof primary?.confidence === 'number' ? primary.confidence : undefined;
  const confidence =
    typeof rawConfidence === 'number' ? Math.max(0, Math.min(1, rawConfidence)) : 0;
  const allOutOfScope =
    !!run.intent_branches?.length && run.intent_branches.every((b) => b.is_out_of_scope);

  const missingEntityBranch = run.intent_branches?.find((b) => b.status === 'missing_entity');
  const hasMissingEntity = Boolean(missingEntityBranch);
  const missingEntities = hasMissingEntity
    ? resolveMissingEntitiesFromBranches(run.intent_branches)
    : [];

  const badgeKind = confidenceToBadge(rawConfidence);
  const status: V1DraftResponse['status'] = allOutOfScope
    ? 'out_of_scope'
    : hasMissingEntity
      ? 'missing_entity'
      : badgeKind === 'low'
        ? 'low_confidence'
        : 'success';
  // V1 schema only allows high/medium/low; map 'unknown' to 'low' so we stay
  // honest about uncertainty rather than guessing a higher tier.
  const badge: V1DraftResponse['badge'] = hasMissingEntity
    ? 'high'
    : badgeKind === 'unknown'
      ? 'low'
      : badgeKind;

  const draftText =
    run.draft?.body?.trim() ||
    (run.error?.message
      ? `Sorry — could not generate a draft (${run.error.message}).`
      : STATUS.fallbackDraft);

  // Dedupe/blank-row/kind/edit_url semantics live in the shared mapper so the
  // Gmail BFF and Outlook adapter stay identical — see @aira/shared citations.
  const citations = toDraftCitations(run.citations);

  return {
    draft: draftText,
    citations,
    knowledge_base_url: run.knowledge_base_url ?? null,
    confidence,
    intent,
    entities: {},
    status,
    kind: allOutOfScope ? 'escalation' : hasMissingEntity ? 'clarification' : 'full',
    trace_id: run.run_id,
    dimensions: [],
    alternatives: [],
    missing_entities: missingEntities,
    tool_failure: null,
    badge,
    candidates: [],
    speculative_alternative: null,
    tool_results: [],
    draft_feedback: run.draft_feedback ?? null,
    channel: 'email',
  };
}
